import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { storageApi } from '@/api/storage.api';
import { useAuth } from '@/context/AuthContext';
import { ViewType } from '@/types/domain';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Badge } from '@/components/ui/Badge';
import { formatBytes } from '@/lib/format';
import { useToasts } from '@/context/ToastContext';
import { ApiError } from '@/lib/errors';

/**
 * V4.9.21 — Admin Storage page. Connect Google Drive from the app (paste the
 * service-account JSON + folder ID — stored encrypted), see disk-vs-Drive
 * counts + quota, Test the connection, and Move ALL existing files to Drive.
 * Admin-only (route redirect + backend both enforce it).
 */
export function StoragePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { push } = useToasts();
  const queryClient = useQueryClient();

  const [saJson, setSaJson] = useState('');
  const [folderId, setFolderId] = useState('');
  const [moving, setMoving] = useState(false);
  const [moveProgress, setMoveProgress] = useState<{ migrated: number; failed: number; freed: number; remaining: number } | null>(null);

  useEffect(() => {
    if (user && user.view_type !== ViewType.Admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const statusQuery = useQuery({
    queryKey: ['storage', 'status'],
    queryFn: () => storageApi.status(),
    enabled: user?.view_type === ViewType.Admin
  });
  const refreshStatus = () => queryClient.invalidateQueries({ queryKey: ['storage', 'status'] });
  const errMsg = (e: unknown) => (e instanceof ApiError ? e.problem?.detail ?? e.message : 'Error');

  const connectMut = useMutation({
    mutationFn: () => storageApi.connect(saJson, folderId),
    onSuccess: (r) => {
      setSaJson(''); // never keep the private key in the browser after saving
      push({
        kind: r.connected ? 'success' : 'error',
        title: r.connected ? 'Google Drive connected' : 'Saved, but connection test failed',
        message: r.message
      });
      void refreshStatus();
    },
    onError: (e: unknown) => push({ kind: 'error', title: 'Connect failed', message: errMsg(e) })
  });

  const disconnectMut = useMutation({
    mutationFn: () => storageApi.disconnect(),
    onSuccess: (r) => { push({ kind: 'success', title: 'Switched to local disk', message: r.message }); void refreshStatus(); },
    onError: (e: unknown) => push({ kind: 'error', title: 'Failed', message: errMsg(e) })
  });

  const testMut = useMutation({
    mutationFn: () => storageApi.test(),
    onSuccess: (r) => push({ kind: r.ok ? 'success' : 'error', title: r.ok ? 'Drive reachable' : 'Drive test failed', message: r.message }),
    onError: (e: unknown) => push({ kind: 'error', title: 'Test failed', message: errMsg(e) })
  });

  async function moveAllToDrive() {
    setMoving(true);
    setMoveProgress(null);
    let migrated = 0, failed = 0, freed = 0, remaining = 0;
    try {
      for (let i = 0; i < 1000; i++) {
        const r = await storageApi.offload(100);
        migrated += r.migrated; failed += r.failed; freed += r.freed_bytes; remaining = r.remaining;
        setMoveProgress({ migrated, failed, freed, remaining });
        if (r.remaining === 0 || r.migrated === 0) break; // done, or the rest can't migrate
      }
      await refreshStatus();
      // Honest outcome: only "success" when the disk is actually clear. If some
      // files remain (e.g. a batch we couldn't verify — usually a missing local
      // file), say so rather than claiming it finished.
      const clean = remaining === 0 && failed === 0;
      push({
        kind: clean ? 'success' : 'warning',
        title: clean ? 'All files moved to Drive' : 'Move to Drive stopped early',
        message: clean
          ? `${migrated} moved · freed ${formatBytes(freed)}`
          : `${migrated} moved · freed ${formatBytes(freed)} · ${remaining} still on disk${failed ? ` · ${failed} could not be migrated (their local file may be missing)` : ''}`
      });
    } catch (e) {
      push({ kind: 'error', title: 'Move to Drive stopped', message: errMsg(e) });
    } finally {
      setMoving(false);
    }
  }

  if (statusQuery.isLoading) return <LoadingState label="Loading storage status..." large />;
  if (statusQuery.isError) return <ErrorState error={statusQuery.error} onRetry={() => statusQuery.refetch()} />;
  const s = statusQuery.data;
  if (!s) return null;

  const connected = s.drive_configured;
  const driveActive = s.active_backend === 'gdrive';
  const localOnDisk = s.counts.local;
  const canConnect = !!saJson.trim() && !!folderId.trim() && s.encryption_ready && !connectMut.isPending;

  return (
    <div>
      <div className="section-header"><h2>Storage</h2></div>

      <div className="detail-grid">
        <div className="flex flex-col gap-4">
          {/* Status */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">Backend</h3></div>
            <div className="card-body">
              <p style={{ marginTop: 0 }}>
                New uploads go to:{' '}
                <Badge tone={driveActive ? 'success' : 'info'}>{driveActive ? 'Google Drive' : 'Local disk'}</Badge>
              </p>
              <p>
                Google Drive:{' '}
                {connected ? <Badge tone="success">Connected</Badge> : <Badge tone="neutral">Not connected</Badge>}
                {s.client_email ? <span className="text-small text-muted"> · {s.client_email}</span> : null}
                {s.config_source === 'env' ? <span className="text-small text-muted"> · via server env</span> : null}
              </p>
              {s.folder_id ? <p className="text-small text-muted" style={{ marginTop: 0 }}>Folder: {s.folder_id}</p> : null}
              {s.credentials_unreadable ? (
                <div className="text-small" style={{ padding: 10, borderRadius: 8, background: '#fef2f2', color: '#991b1b', margin: '8px 0' }}>
                  Stored Google Drive credentials could not be decrypted (the server's
                  encryption key is missing or was changed). New uploads are using local
                  disk until you reconnect. Paste the service-account key again below to fix.
                </div>
              ) : null}
              {connected ? (
                <div className="flex gap-2" style={{ marginTop: 10, flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={testMut.isPending} onClick={() => testMut.mutate()}>
                    {testMut.isPending ? 'Testing…' : 'Test connection'}
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={disconnectMut.isPending}
                    onClick={() => { if (window.confirm('Switch new uploads back to local disk? Files already on Drive stay accessible.')) disconnectMut.mutate(); }}>
                    Switch to local disk
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {/* Connect form */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">{connected ? 'Update Google Drive credentials' : 'Connect Google Drive'}</h3></div>
            <div className="card-body">
              {!s.encryption_ready ? (
                <div className="text-small" style={{ padding: 10, borderRadius: 8, background: '#fef2f2', color: '#991b1b', marginBottom: 12 }}>
                  Before connecting, set a one-time encryption key on the server so your key is stored encrypted:
                  add <code>WEBHOOK_SECRET_ENCRYPTION_KEY=&lt;64-hex-chars&gt;</code> to <code>.env</code> and restart
                  (generate with <code>openssl rand -hex 32</code>).
                </div>
              ) : null}
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Service-account JSON key</label>
                <textarea
                  className="form-textarea"
                  rows={6}
                  placeholder='Paste the whole downloaded service-account .json here, e.g. {"type":"service_account","project_id":"…","private_key":"-----BEGIN PRIVATE KEY-----…","client_email":"…@….iam.gserviceaccount.com", …}'
                  value={saJson}
                  onChange={(e) => setSaJson(e.target.value)}
                  spellCheck={false}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                />
              </div>
              <div className="form-group" style={{ marginTop: 10 }}>
                <label className="form-label">Google Drive folder ID</label>
                <input
                  className="form-input"
                  placeholder="From the folder URL: drive.google.com/drive/folders/<THIS-ID>"
                  value={folderId}
                  onChange={(e) => setFolderId(e.target.value)}
                />
              </div>
              <div className="flex gap-2" style={{ marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-primary" disabled={!canConnect} onClick={() => connectMut.mutate()}>
                  {connectMut.isPending ? 'Connecting…' : connected ? 'Update & test' : 'Connect & test'}
                </button>
                <span className="text-small text-muted">The key is stored encrypted and never shown again.</span>
              </div>
            </div>
          </div>

          {/* Move everything to Drive */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">Move ALL existing files to Google Drive</h3></div>
            <div className="card-body">
              <p style={{ marginTop: 0 }} className="text-small text-muted">
                Uploads every on-disk attachment to Drive and frees the server space. Each file is uploaded and
                size-verified on Drive BEFORE its local copy is removed. Runs in batches until the disk is clear.
              </p>
              <div className="flex gap-2" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" className="btn btn-primary" disabled={moving || !connected || localOnDisk === 0} onClick={() => moveAllToDrive()}>
                  {moving ? 'Moving…' : `Move ${localOnDisk} file${localOnDisk === 1 ? '' : 's'} to Drive`}
                </button>
                {!connected ? <span className="text-small text-muted">Connect Drive first.</span> : null}
                {connected && localOnDisk === 0 ? <span className="text-small text-muted">Nothing on disk — all attachments are on Drive 🎉</span> : null}
              </div>
              {moveProgress ? (
                <div className="text-small" style={{ marginTop: 10 }}>
                  Moved <strong>{moveProgress.migrated}</strong> · freed {formatBytes(moveProgress.freed)} ·{' '}
                  <strong>{moveProgress.remaining}</strong> left{moveProgress.failed ? ` · ${moveProgress.failed} failed` : ''}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <aside className="flex flex-col gap-4">
          <div className="card">
            <div className="card-header"><h3 className="card-title">Files</h3></div>
            <div className="card-body">
              <div className="flex gap-4" style={{ flexWrap: 'wrap' }}>
                <StatBox label="On server disk" value={String(localOnDisk)} />
                <StatBox label="On Google Drive" value={String(s.counts.gdrive)} />
              </div>
              <div className="text-small text-muted" style={{ marginTop: 10 }}>
                Disk used: {s.local_disk_bytes != null ? formatBytes(s.local_disk_bytes) : '—'}
              </div>
              {s.drive_quota && s.drive_quota.limit != null ? (
                <div className="text-small text-muted">
                  Drive quota: {formatBytes(s.drive_quota.usage ?? 0)} of {formatBytes(s.drive_quota.limit)}
                </div>
              ) : null}
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h3 className="card-title">How to get the key</h3></div>
            <div className="card-body text-small">
              <ol style={{ paddingLeft: 18, margin: 0 }}>
                <li>Google Cloud → enable the <strong>Drive API</strong>.</li>
                <li>Create a <strong>service account</strong> → Keys → add key → JSON → download.</li>
                <li>Share a Drive folder (or Shared Drive) with the service account's email.</li>
                <li>Paste the JSON + the folder ID on the left → <strong>Connect &amp; test</strong>.</li>
                <li>Then <strong>Move ALL existing files to Drive</strong> to free the server disk.</li>
              </ol>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ minWidth: 130 }}>
      <div className="stat-value" style={{ fontSize: 26, fontWeight: 800 }}>{value}</div>
      <div className="text-small text-muted">{label}</div>
    </div>
  );
}
